import { TrashAlt } from '@assets/svgs';
import { DataGrid } from '@components/dataGrid/Datagrid';
import { Dialog } from '@components/dialog/Dialog';
import { IconButton } from '@components/iconButton';
import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { OverflowTooltip } from '@components/tooltip';
import { SubjectivityItem } from '@services/subjectivity';

type ButtonGroupProps = {
  open: boolean;
  label?: string;
  documents?: SubjectivityItem['documents'];
  handleClose: () => void;
  handleDeleteFile?: (id: string) => void;
  disableDeleteLastFile: boolean;
};

enum ViewFilesFieldNames {
  FileName = 'fileName',
  Delete = 'delete',
}

const viewFilesColumns: GridColDef[] = [
  {
    field: ViewFilesFieldNames.FileName,
    headerName: '',
    sortable: false,
    flex: 1,
    minWidth: 40,
    renderCell: ({ row }) => (
      <OverflowTooltip
        tooltipText={row.file.fileName}
        fontColor={row.file.downloadUrl ? 'secondary.main' : 'text.disabled'}
      >
        <Typography
          variant='body2'
          component='a'
          target='_blank'
          href={row.file.downloadUrl}
          paddingLeft={2}
          sx={{
            color: row.file.downloadUrl ? 'secondary.main' : 'text.disabled',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: row.file.downloadUrl ? 'underline' : 'none',
            },
          }}
        >
          {row.file.fileName}
        </Typography>
      </OverflowTooltip>
    ),
  },
  {
    field: ViewFilesFieldNames.Delete,
    headerName: '',
    sortable: false,
    width: 1,
    renderCell: ({ row }) => {
      const { handleDeleteFile } = row;

      if (!handleDeleteFile) {
        return null;
      }
      return (
        <IconButton
          title='deleteFile'
          size='small'
          onClick={() => handleDeleteFile(row.id)}
          sx={{ svg: { width: '10px' } }}
        >
          <TrashAlt />
        </IconButton>
      );
    },
  },
];

export const ViewFilesDialog = ({
  disableDeleteLastFile,
  open,
  label,
  documents,
  handleClose,
  handleDeleteFile,
}: ButtonGroupProps) => {
  const disableDelete = disableDeleteLastFile && documents?.length === 1;

  const data =
    documents?.map((el) => ({
      ...el,
      handleDeleteFile: disableDelete ? undefined : () => handleDeleteFile?.(el.id),
    })) ?? [];

  return (
    <Dialog
      size='small'
      open={open}
      title={`${label} Files`}
      handleCloseDialog={handleClose}
      secondaryBtnText='Exit'
      secondaryBtnAction={handleClose}
    >
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='space-between'
        sx={{
          width: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: '36px',
          height: '100%',
        }}
      >
        <DataGrid
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter
          columns={viewFilesColumns}
          rows={data}
          rowCount={documents?.length}
          initialState={{ density: 'compact' }}
          columnHeaderHeight={0}
          sx={{
            ...styles.dataGrid,
            height: 'fit-content',
            border: 'none',
            '& .MuiDataGrid-row': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              border: 'none',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
          }}
        />
      </Box>
    </Dialog>
  );
};
